import { useState } from "react";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { templateApi } from "../../../api/templates";
import type { ShiftAdmin } from "../../../api/types";
import { Button, ErrorText, TextInput } from "../../../components/ui";

export function SeriesPanel({ s, onChanged }: { s: ShiftAdmin; onChanged: () => void }) {
  const [count, setCount] = useState<number | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  if (!s.template_id && !s.series_id) return null;

  const start = async () => {
    setError(null);
    try { setCount((await templateApi.series(s.series_id!)).cancellable); } catch (e) { setError(errorMessage(e)); }
  };
  const cancel = async () => {
    setBusy(true); setError(null);
    try {
      const r = await templateApi.cancelSeries(s.series_id!, reason);
      toast.success(`Cancelled ${r.cancelled} shift${r.cancelled === 1 ? "" : "s"}`);
      setCount(null); onChanged();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <section data-testid="drawer-template-section" className="space-y-2 rounded-sm border border-white/10 p-4 text-sm">
      {s.template_id && <p data-testid="drawer-template-name" className="text-zinc-200">Created from template <span className="font-medium text-white">{s.template_name ?? "(deleted)"}</span></p>}
      {s.series_id && <p data-testid="drawer-series-count" className="text-zinc-400">Part of series ({s.series_count ?? 0} shifts)</p>}
      {s.series_id && count === null && (
        <Button variant="ghost" className="h-9 px-0 text-[11px] hover:text-signal" onClick={start} data-testid="drawer-series-cancel-button">Cancel remaining shifts in this series</Button>
      )}
      {count !== null && (
        <div className="space-y-2" data-testid="drawer-series-cancel-confirm">
          <p data-testid="drawer-series-cancel-count" className="text-zinc-200">{count} future, un-worked shift{count === 1 ? "" : "s"} will be cancelled. Past, worked and locked-period shifts are not touched.</p>
          <TextInput placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="drawer-series-cancel-reason" />
          <div className="flex gap-2">
            <Button variant="ghost" className="h-9 px-4 text-[11px]" onClick={() => setCount(null)} data-testid="drawer-series-cancel-back">Keep</Button>
            <Button className="h-9 px-4 text-[11px]" disabled={busy || count === 0 || reason.trim().length < 3} onClick={cancel} data-testid="drawer-series-cancel-submit">Cancel {count} shifts</Button>
          </div>
        </div>
      )}
      <ErrorText message={error} testId="drawer-series-error" />
    </section>
  );
}
